import React, { useEffect, useState } from "react";
import { Box, InputLabel, TextField } from "@mui/material";
import { palette } from "../../../styles/theme";
import { formatClickToEditValue, getDebugLabelProps } from "./shared";

export function SpecsInputField({
  label,
  sx,
  debugPath,
  clickToEdit,
  closeOnBlur = true,
  ...props
}: React.ComponentProps<typeof TextField> & {
  label: React.ReactNode;
  debugPath?: string;
  clickToEdit?: boolean;
  closeOnBlur?: boolean;
}) {
  const debugLabelProps = getDebugLabelProps(debugPath);
  const [editing, setEditing] = useState(!clickToEdit);

  useEffect(() => {
    if (!clickToEdit) {
      setEditing(true);
    }
  }, [clickToEdit]);

  const displayValue =
    props.value === undefined ? "" : formatClickToEditValue(props.value);

  return (
    <Box sx={sx}>
      <InputLabel
        onClick={debugLabelProps.onClick}
        sx={{
          mb: 0.5,
          fontSize: 13,
          ...debugLabelProps.sx,
        }}
      >
        {label}
      </InputLabel>
      {editing ? (
        <TextField
          {...props}
          autoFocus={clickToEdit ? true : props.autoFocus}
          fullWidth
          size="small"
          variant="standard"
          InputProps={{ ...props.InputProps, disableUnderline: true }}
          onBlur={(event) => {
            props.onBlur?.(event);
            if (clickToEdit && closeOnBlur) {
              setEditing(false);
            }
          }}
        />
      ) : (
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setEditing(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setEditing(true);
            }
          }}
          sx={{
            width: "100%",
            minHeight: props.multiline ? 88 : 38,
            display: "flex",
            alignItems: props.multiline ? "flex-start" : "center",
            gap: 0.75,
            px: 1.5,
            py: props.multiline ? 1.25 : 1,
            borderRadius: "6px",
            border: `1px solid ${palette.grey.lightGrey}`,
            bgcolor: "common.white",
            cursor: "text",
            color: displayValue.trim() ? "text.primary" : "text.secondary",
            fontSize: 14,
            lineHeight: 1.4,
            "&:hover": {
              borderColor: palette.grey.darkGrey,
            },
          }}
        >
          {props.InputProps?.startAdornment ? (
            <Box
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                color: "text.secondary",
              }}
            >
              {props.InputProps.startAdornment}
            </Box>
          ) : null}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              whiteSpace: props.multiline ? "pre-wrap" : "nowrap",
              overflow: "hidden",
              textOverflow: props.multiline ? undefined : "ellipsis",
            }}
          >
            {displayValue.trim() ? displayValue : "Click to edit"}
          </Box>
          {props.InputProps?.endAdornment ? (
            <Box
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                color: "text.secondary",
              }}
            >
              {props.InputProps.endAdornment}
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
}
